// ─────────────────────────────────────────────────────────────────────────
// Import Menu Dialog — Phase 2 of the CSV menu import/export feature.
//
// Two-step flow:
//   1. File picker + dry-run preview (shows stats + per-row diff)
//   2. Apply button → auto-backup → write to Firestore → success + undo
//
// All writes go through firebaseAdminService so existing guard logic
// (e.g. orders filter) is unchanged.
// ─────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Typography, Button,
  IconButton, Alert, Chip, LinearProgress, Divider,
} from '@mui/material';
import { Cancel, FileUpload, WarningAmber, CheckCircle, Info, History, Undo } from '@mui/icons-material';
import firebaseAdminService, { AdminMenuItem } from '../../services/firebaseAdminService';
import {
  parseMenuCsv, buildDiff, applyImport, backupCurrentMenu,
  listRecentBackups, restoreBackup,
  DiffReport, DiffRow, BackupMetadata, ValidationError,
} from '../../services/menuImportService';

interface ImportMenuDialogProps {
  open: boolean;
  onClose: () => void;
  existingItems: AdminMenuItem[];
  categories: Array<{ name: string }>;
  currentUserEmail?: string;
  onImportComplete: () => void;
}

type Step = 'pick' | 'preview' | 'applying' | 'done';

const ImportMenuDialog: React.FC<ImportMenuDialogProps> = ({
  open, onClose, existingItems, categories, currentUserEmail, onImportComplete,
}) => {
  const [step, setStep] = useState<Step>('pick');
  const [file, setFile] = useState<File | null>(null);
  const [fileText, setFileText] = useState<string>('');
  const [headerWarnings, setHeaderWarnings] = useState<string[]>([]);
  const [parseErrors, setParseErrors] = useState<ValidationError[]>([]);
  const [diffReport, setDiffReport] = useState<DiffReport | null>(null);
  const [skipConflicts, setSkipConflicts] = useState(true);
  // Default ON: a CSV with only a handful of rows should never silently wipe
  // the rest of the menu. Client has to explicitly opt into deletions via the
  // toggle in the delete-warning banner.
  const [skipDeletes, setSkipDeletes] = useState(true);
  const [applyResult, setApplyResult] = useState<Awaited<ReturnType<typeof applyImport>> | null>(null);
  const [backupId, setBackupId] = useState<string | null>(null);
  const [recentBackups, setRecentBackups] = useState<BackupMetadata[]>([]);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStep('pick');
    setFile(null);
    setFileText('');
    setHeaderWarnings([]);
    setParseErrors([]);
    setDiffReport(null);
    setApplyResult(null);
    setBackupId(null);
    setError(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFileSelect = async (f: File) => {
    setError(null);
    setFile(f);
    try {
      const text = await f.text();
      setFileText(text);
      const parsed = parseMenuCsv(text);
      setHeaderWarnings(parsed.headerWarnings);
      setParseErrors(parsed.validationErrors);
      if (parsed.validationErrors.length > 0 && parsed.rows.length === 0) {
        setStep('pick');
        return;
      }
      const report = buildDiff(parsed.rows, existingItems, categories.map(c => c.name));
      // Merge row-level parse errors + category errors from buildDiff
      report.validationErrors = [...parsed.validationErrors, ...report.validationErrors];
      setDiffReport(report);
      setStep('preview');
    } catch (e: any) {
      setError(`Failed to read file: ${e?.message || e}`);
    }
  };

  const handleApply = async () => {
    if (!diffReport) return;
    setStep('applying');
    setError(null);
    try {
      // 1. Auto-backup first
      const bId = await backupCurrentMenu(existingItems, {
        author: currentUserEmail,
        source_filename: file?.name,
        reason: 'menu_import',
      });
      setBackupId(bId);

      // 2. Apply the diff
      const result = await applyImport(diffReport.diff, {
        skipConflicts,
        skipDeletes,
        backupAuthor: currentUserEmail,
        sourceFilename: file?.name,
      });
      setApplyResult(result);
      setStep('done');
      onImportComplete();
    } catch (e: any) {
      setError(`Import failed: ${e?.message || e}`);
      setStep('preview');
    }
  };

  const handleShowBackups = async () => {
    try {
      const list = await listRecentBackups(10);
      setRecentBackups(list);
    } catch (e: any) {
      setError(`Failed to load backups: ${e?.message}`);
    }
  };

  const handleRestore = async (backupToRestore: string) => {
    if (!window.confirm('Restore this menu snapshot? This will overwrite all current menu items with the backup version.')) return;
    setRestoring(true);
    setError(null);
    try {
      const res = await restoreBackup(backupToRestore);
      alert(`Restored ${res.restored} items.${res.errors.length ? `\n\n${res.errors.length} errors — see console.` : ''}`);
      onImportComplete();
      handleClose();
    } catch (e: any) {
      setError(`Restore failed: ${e?.message}`);
    } finally {
      setRestoring(false);
    }
  };

  const dialogPaperSx = {
    backgroundColor: '#0b0d18',
    backgroundImage: 'linear-gradient(180deg, #111427 0%, #0b0d18 100%)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 3, color: '#f0f2f5', maxHeight: '92vh',
  };

  // ── Stats summary card ──
  const statsCard = diffReport && (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mb: 2 }}>
      {[
        { label: 'Add', value: diffReport.stats.add, color: '#4ade80' },
        { label: 'Update', value: diffReport.stats.update, color: '#fbbf24' },
        { label: 'Delete', value: diffReport.stats.delete, color: '#f87171' },
        { label: 'Unchanged', value: diffReport.stats.unchanged, color: '#6b7280' },
        { label: 'Conflicts', value: diffReport.stats.conflicts, color: '#f97316' },
      ].map(s => (
        <Box key={s.label} sx={{
          p: 1.5, backgroundColor: 'rgba(22,25,40,0.7)',
          border: `1px solid ${s.color}33`, borderRadius: 2, textAlign: 'center',
        }}>
          <Typography sx={{ fontFamily: '"Poppins"', fontSize: '1.6rem', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</Typography>
          <Typography sx={{ fontFamily: '"Poppins"', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.8px', mt: 0.5 }}>{s.label}</Typography>
        </Box>
      ))}
    </Box>
  );

  // ── Diff row renderer ──
  const renderDiffRow = (row: DiffRow, idx: number) => {
    const colorMap = {
      add: { bg: 'rgba(74,222,128,0.08)', border: 'rgba(74,222,128,0.3)', label: 'ADD', labelBg: '#4ade80' },
      update: { bg: 'rgba(251,191,36,0.08)', border: 'rgba(251,191,36,0.3)', label: 'UPDATE', labelBg: '#fbbf24' },
      delete: { bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.3)', label: 'DELETE', labelBg: '#f87171' },
      unchanged: { bg: 'rgba(107,114,128,0.04)', border: 'rgba(107,114,128,0.15)', label: 'SAME', labelBg: '#6b7280' },
    }[row.action];

    const name = row.csvRow?.nameEn || row.existingItem?.name || '?';
    const category = row.csvRow?.category || row.existingItem?.category || '';

    return (
      <Box key={idx} sx={{
        p: 1.2, mb: 0.8, borderRadius: 2,
        backgroundColor: colorMap.bg, border: `1px solid ${colorMap.border}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ px: 0.8, py: 0.25, borderRadius: 0.8, backgroundColor: colorMap.labelBg, color: '#0b0d18', fontFamily: '"Poppins"', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.5px', minWidth: 52, textAlign: 'center' }}>{colorMap.label}</Box>
          <Typography sx={{ fontFamily: '"Poppins"', fontSize: '0.85rem', fontWeight: 700, color: '#f0f2f5', flex: 1 }}>{name}</Typography>
          {category && <Chip size="small" label={category} sx={{ backgroundColor: 'rgba(255,255,255,0.04)', color: '#9ca3af', fontFamily: '"Poppins"', fontSize: '0.65rem' }} />}
          {row.conflict && (
            <Chip size="small" icon={<WarningAmber sx={{ fontSize: 14, color: '#f97316 !important' }} />} label="CONFLICT" sx={{ backgroundColor: 'rgba(249,115,22,0.15)', color: '#f97316', fontFamily: '"Poppins"', fontSize: '0.65rem', fontWeight: 700 }} />
          )}
        </Box>
        {row.changes && row.changes.length > 0 && (
          <Box sx={{ mt: 0.6, ml: 7, fontFamily: '"Poppins"', fontSize: '0.72rem', color: '#9ca3af' }}>
            {row.changes.slice(0, 5).map((c, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                <Typography component="span" sx={{ color: '#6b7280', fontSize: 'inherit', fontFamily: 'inherit', minWidth: 100 }}>{c.field}:</Typography>
                <Typography component="span" sx={{ color: '#f87171', fontSize: 'inherit', fontFamily: 'inherit', textDecoration: 'line-through' }}>
                  {String(c.oldValue ?? '∅').substring(0, 40)}
                </Typography>
                <Typography component="span" sx={{ color: '#9ca3af', fontSize: 'inherit', fontFamily: 'inherit' }}>→</Typography>
                <Typography component="span" sx={{ color: '#4ade80', fontSize: 'inherit', fontFamily: 'inherit' }}>
                  {String(c.newValue ?? '∅').substring(0, 40)}
                </Typography>
              </Box>
            ))}
            {row.changes.length > 5 && (
              <Typography sx={{ fontFamily: 'inherit', fontSize: 'inherit', color: '#6b7280', mt: 0.25 }}>
                + {row.changes.length - 5} more fields
              </Typography>
            )}
          </Box>
        )}
        {row.conflict && (
          <Typography sx={{ mt: 0.6, ml: 7, fontFamily: '"Poppins"', fontSize: '0.7rem', color: '#f97316' }}>
            ⚠ {row.conflict.description}
          </Typography>
        )}
      </Box>
    );
  };

  // Visible diff — hide unchanged by default to reduce scroll
  const [showUnchanged, setShowUnchanged] = useState(false);
  const visibleDiff = useMemo(() => {
    if (!diffReport) return [];
    // Priority: conflicts first, then delete, add, update, unchanged
    const order = { delete: 0, add: 1, update: 2, unchanged: 3 };
    return diffReport.diff
      .filter(r => showUnchanged || r.action !== 'unchanged')
      .sort((a, b) => {
        if (a.conflict && !b.conflict) return -1;
        if (!a.conflict && b.conflict) return 1;
        return order[a.action] - order[b.action];
      });
  }, [diffReport, showUnchanged]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
      BackdropProps={{ sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(3,5,12,0.75)' } }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 2, backgroundColor: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FileUpload sx={{ color: '#60a5fa' }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: '"Poppins"', fontWeight: 700, fontSize: '1.1rem', color: '#f0f2f5' }}>Import Menu</Typography>
            <Typography sx={{ fontFamily: '"Poppins"', fontSize: '0.72rem', color: '#6b7280' }}>
              {step === 'pick' && 'Upload a CSV file exported from this admin panel or compatible format'}
              {step === 'preview' && 'Review the changes before applying — nothing is written yet'}
              {step === 'applying' && 'Writing changes to Firestore…'}
              {step === 'done' && 'Import complete'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: '#9ca3af', '&:hover': { color: '#f0f2f5', backgroundColor: 'rgba(255,255,255,0.06)' } }}><Cancel /></IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Step 1: Pick ── */}
        {step === 'pick' && (
          <Box>
            <Box sx={{
              p: 4, border: '2px dashed rgba(96,165,250,0.3)', borderRadius: 3,
              backgroundColor: 'rgba(96,165,250,0.04)', textAlign: 'center', cursor: 'pointer',
              transition: 'all 0.15s',
              '&:hover': { borderColor: '#60a5fa', backgroundColor: 'rgba(96,165,250,0.08)' },
            }} onClick={() => fileInputRef.current?.click()}>
              <FileUpload sx={{ fontSize: 48, color: '#60a5fa', mb: 1 }} />
              <Typography sx={{ fontFamily: '"Poppins"', fontSize: '0.95rem', fontWeight: 700, color: '#f0f2f5' }}>Click to select a CSV file</Typography>
              <Typography sx={{ fontFamily: '"Poppins"', fontSize: '0.78rem', color: '#9ca3af', mt: 0.5 }}>
                …or drag and drop. File must use the Saheeh export format.
              </Typography>
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); e.target.value = ''; }}
            />

            {parseErrors.length > 0 && (
              <Alert severity="error" icon={<WarningAmber />} sx={{ mt: 2 }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{parseErrors.length} validation error{parseErrors.length === 1 ? '' : 's'}</Typography>
                <Box sx={{ maxHeight: 160, overflowY: 'auto', fontFamily: '"Poppins"', fontSize: '0.78rem' }}>
                  {parseErrors.slice(0, 20).map((e, i) => (
                    <Typography key={i} sx={{ fontFamily: 'inherit', fontSize: 'inherit' }}>
                      Line {e.lineNumber} · {e.field}: {e.message}
                    </Typography>
                  ))}
                  {parseErrors.length > 20 && <Typography sx={{ fontFamily: 'inherit', fontSize: 'inherit', fontStyle: 'italic' }}>… and {parseErrors.length - 20} more.</Typography>}
                </Box>
              </Alert>
            )}

            {/* Recent backups / restore section */}
            <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.06)' }} />
            <Box>
              <Button
                onClick={handleShowBackups}
                startIcon={<History />}
                sx={{ fontFamily: '"Poppins"', textTransform: 'none', color: '#9ca3af', fontSize: '0.8rem', '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' } }}
              >
                Recent menu backups (for undo)
              </Button>
              {recentBackups.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {recentBackups.map((b) => (
                    <Box key={b.id} sx={{ p: 1.2, mb: 0.8, backgroundColor: 'rgba(22,25,40,0.7)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontFamily: '"Poppins"', fontSize: '0.82rem', fontWeight: 600, color: '#f0f2f5' }}>
                          {new Date(b.created_at).toLocaleString('de-DE')} — {b.item_count} items
                        </Typography>
                        <Typography sx={{ fontFamily: '"Poppins"', fontSize: '0.68rem', color: '#6b7280' }}>
                          {b.reason === 'menu_import' ? `Auto-backup before import${b.source_filename ? ` (${b.source_filename})` : ''}` : 'Manual backup'} {b.author ? ` · ${b.author}` : ''}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        startIcon={<Undo />}
                        disabled={restoring}
                        onClick={() => handleRestore(b.id)}
                        sx={{ fontFamily: '"Poppins"', textTransform: 'none', color: '#f59e0b', borderColor: 'rgba(245,158,11,0.35)', fontSize: '0.75rem', '&:hover': { borderColor: '#f59e0b', backgroundColor: 'rgba(245,158,11,0.08)' } }}
                        variant="outlined"
                      >
                        Restore
                      </Button>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* ── Step 2: Preview ── */}
        {step === 'preview' && diffReport && (
          <Box>
            {statsCard}

            {headerWarnings.length > 0 && (
              <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Header notes:</Typography>
                {headerWarnings.map((w, i) => <Typography key={i} sx={{ fontSize: '0.78rem' }}>• {w}</Typography>)}
              </Alert>
            )}

            {diffReport.validationErrors.length > 0 && (
              <Alert severity="error" icon={<WarningAmber />} sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{diffReport.validationErrors.length} row{diffReport.validationErrors.length === 1 ? '' : 's'} with errors — these rows will be skipped:</Typography>
                <Box sx={{ maxHeight: 120, overflowY: 'auto', fontSize: '0.78rem' }}>
                  {diffReport.validationErrors.slice(0, 20).map((e, i) => (
                    <Typography key={i} sx={{ fontFamily: '"Poppins"', fontSize: 'inherit' }}>Line {e.lineNumber} · {e.field}: {e.message}</Typography>
                  ))}
                  {diffReport.validationErrors.length > 20 && <Typography sx={{ fontFamily: '"Poppins"', fontSize: 'inherit', fontStyle: 'italic' }}>… and {diffReport.validationErrors.length - 20} more.</Typography>}
                </Box>
              </Alert>
            )}

            {diffReport.stats.conflicts > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography sx={{ fontFamily: '"Poppins"' }}>
                  {diffReport.stats.conflicts} item{diffReport.stats.conflicts === 1 ? '' : 's'} changed on the server after your export.{' '}
                  {skipConflicts ? 'They will be SKIPPED to protect server-side changes.' : 'They will be OVERWRITTEN.'}
                  <Button size="small" onClick={() => setSkipConflicts(!skipConflicts)} sx={{ fontFamily: '"Poppins"', textTransform: 'none', ml: 1, color: '#f97316' }}>
                    {skipConflicts ? 'Overwrite anyway' : 'Skip them (recommended)'}
                  </Button>
                </Typography>
              </Alert>
            )}

            {diffReport.stats.delete > 0 && (
              <Alert severity={skipDeletes ? 'info' : 'error'} sx={{ mb: 2 }}>
                <Typography sx={{ fontFamily: '"Poppins"', fontWeight: 700 }}>
                  {diffReport.stats.delete} item{diffReport.stats.delete === 1 ? '' : 's'} in the current menu are missing from this CSV.
                </Typography>
                <Typography sx={{ fontFamily: '"Poppins"', fontSize: '0.82rem', mt: 0.5 }}>
                  {skipDeletes
                    ? 'They will be KEPT. Use this mode for partial uploads (e.g. adding/updating a few items) — the rest of your menu stays untouched.'
                    : '⚠ They will be PERMANENTLY DELETED. Only enable this if your CSV is a complete replacement for the whole menu.'}
                  <Button
                    size="small"
                    onClick={() => {
                      if (skipDeletes) {
                        const ok = window.confirm(
                          `You are about to DELETE ${diffReport.stats.delete} menu item(s) that are not in this CSV.\n\nA backup is taken automatically, but this still affects every storefront immediately.\n\nContinue?`
                        );
                        if (!ok) return;
                      }
                      setSkipDeletes(!skipDeletes);
                    }}
                    sx={{ fontFamily: '"Poppins"', textTransform: 'none', ml: 1, color: skipDeletes ? '#f87171' : '#00C896' }}
                  >
                    {skipDeletes ? 'Enable deletions (full replace)' : 'Keep them (recommended)'}
                  </Button>
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography sx={{ fontFamily: '"Poppins"', fontSize: '0.82rem', fontWeight: 700, color: '#f0f2f5' }}>Changes</Typography>
              <Button size="small" onClick={() => setShowUnchanged(!showUnchanged)} sx={{ fontFamily: '"Poppins"', textTransform: 'none', color: '#9ca3af', fontSize: '0.72rem' }}>
                {showUnchanged ? 'Hide unchanged' : `Show ${diffReport.stats.unchanged} unchanged`}
              </Button>
            </Box>

            <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
              {visibleDiff.map((row, idx) => renderDiffRow(row, idx))}
            </Box>
          </Box>
        )}

        {/* ── Step 3: Applying ── */}
        {step === 'applying' && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <LinearProgress sx={{ mb: 2, '& .MuiLinearProgress-bar': { backgroundColor: '#60a5fa' } }} />
            <Typography sx={{ fontFamily: '"Poppins"', color: '#9ca3af', fontSize: '0.85rem' }}>Writing changes to Firestore… please wait.</Typography>
          </Box>
        )}

        {/* ── Step 4: Done ── */}
        {step === 'done' && applyResult && (
          <Box sx={{ py: 2 }}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircle sx={{ fontSize: 56, color: '#4ade80', mb: 1 }} />
              <Typography sx={{ fontFamily: '"Poppins"', fontSize: '1.2rem', fontWeight: 800, color: '#f0f2f5' }}>Import complete</Typography>
              <Typography sx={{ fontFamily: '"Poppins"', fontSize: '0.85rem', color: '#9ca3af', mt: 0.5 }}>
                {applyResult.added} added · {applyResult.updated} updated · {applyResult.deleted} deleted · {applyResult.skipped} skipped
              </Typography>
            </Box>

            {backupId && (
              <Alert severity="success" icon={<Info />} sx={{ mb: 2 }}>
                <Typography sx={{ fontFamily: '"Poppins"', fontSize: '0.82rem' }}>
                  Auto-backup saved as <code>{backupId}</code>. You can restore it anytime from the Import dialog's "Recent backups" section.
                </Typography>
              </Alert>
            )}

            {applyResult.errors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{applyResult.errors.length} error{applyResult.errors.length === 1 ? '' : 's'}:</Typography>
                <Box sx={{ maxHeight: 160, overflowY: 'auto', fontSize: '0.78rem' }}>
                  {applyResult.errors.map((e, i) => <Typography key={i} sx={{ fontFamily: '"Poppins"', fontSize: 'inherit' }}>{e}</Typography>)}
                </Box>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        {step === 'pick' && (
          <Button onClick={handleClose} sx={{ fontFamily: '"Poppins"', textTransform: 'none', color: '#9ca3af' }}>Close</Button>
        )}
        {step === 'preview' && (
          <>
            <Button onClick={reset} sx={{ fontFamily: '"Poppins"', textTransform: 'none', color: '#9ca3af' }}>Choose different file</Button>
            <Button onClick={handleClose} sx={{ fontFamily: '"Poppins"', textTransform: 'none', color: '#9ca3af' }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={(() => {
                if (!diffReport) return true;
                // Block Apply only for duplicate Firestore IDs — two rows with
                // the same ID would overwrite the same document silently. Item
                // Number duplicates are allowed (cosmetic prefix, not a key);
                // they surface as warnings instead.
                const hasHardDupes = diffReport.validationErrors.some(e => e.field === 'Firestore ID');
                if (hasHardDupes) return true;
                const work = diffReport.stats.add + diffReport.stats.update + (skipDeletes ? 0 : diffReport.stats.delete);
                return work === 0;
              })()}
              sx={{ fontFamily: '"Poppins"', textTransform: 'none', fontWeight: 700, backgroundColor: '#60a5fa', color: '#0b0d18', borderRadius: 2, px: 3, '&:hover': { backgroundColor: '#3b82f6' } }}
            >
              Apply changes
            </Button>
          </>
        )}
        {step === 'done' && (
          <Button onClick={handleClose} variant="contained" sx={{ fontFamily: '"Poppins"', textTransform: 'none', fontWeight: 700, backgroundColor: '#00C896', color: '#0b0d18', borderRadius: 2, px: 3 }}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportMenuDialog;
